import { baseApi, PaginatedResponse } from 'sama-shared';
import type {
  PaymentMethod,
  GetPaymentMethodsParams,
  CreatePaymentMethodDto,
  UpdatePaymentMethodDto,
  ReorderPaymentMethodsDto,
} from './types/payment-method';

const LIST = { type: 'PaymentMethod' as const, id: 'LIST' };

export const paymentMethodApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPaymentMethods: builder.query<PaginatedResponse<PaymentMethod>, GetPaymentMethodsParams | void>({
      query: (params) => ({
        url: '/admin/payment-method',
        method: 'GET',
        params: params || {},
      }),
      providesTags: (result) =>
        result
          ? [...result.data.map(({ id }) => ({ type: 'PaymentMethod' as const, id })), LIST]
          : [LIST],
    }),

    createPaymentMethod: builder.mutation<PaymentMethod, CreatePaymentMethodDto>({
      query: (body) => ({ url: '/admin/payment-method', method: 'POST', body }),
      invalidatesTags: [LIST],
    }),

    updatePaymentMethod: builder.mutation<
      PaymentMethod,
      { id: string; body: UpdatePaymentMethodDto }
    >({
      query: ({ id, body }) => ({ url: `/admin/payment-method/${id}`, method: 'PATCH', body }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'PaymentMethod', id }, LIST],
    }),

    updatePaymentMethodStatus: builder.mutation<PaymentMethod, { id: string; isActive: boolean }>({
      query: ({ id, isActive }) => ({
        url: `/admin/payment-method/${id}/status`,
        method: 'PATCH',
        body: { isActive },
      }),
      invalidatesTags: (_r, _e, { id }) => [{ type: 'PaymentMethod', id }, LIST],
    }),

    reorderPaymentMethods: builder.mutation<{ updated: number }, ReorderPaymentMethodsDto>({
      query: (body) => ({ url: '/admin/payment-method/reorder', method: 'PATCH', body }),
      invalidatesTags: [LIST],
    }),

    uploadPaymentMethodLogo: builder.mutation<PaymentMethod, { id: string; file: File }>({
      query: ({ id, file }) => {
        const body = new FormData();
        body.append('file', file); // sin Content-Type manual: el navegador pone el boundary
        return { url: `/admin/payment-method/${id}/logo`, method: 'POST', body };
      },
      invalidatesTags: (_r, _e, { id }) => [{ type: 'PaymentMethod', id }, LIST],
    }),

    deletePaymentMethodLogo: builder.mutation<void, string>({
      query: (id) => ({ url: `/admin/payment-method/${id}/logo`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [{ type: 'PaymentMethod', id }, LIST],
    }),

    deletePaymentMethod: builder.mutation<void, string>({
      query: (id) => ({ url: `/admin/payment-method/${id}`, method: 'DELETE' }),
      invalidatesTags: [LIST],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetPaymentMethodsQuery,
  useCreatePaymentMethodMutation,
  useUpdatePaymentMethodMutation,
  useUpdatePaymentMethodStatusMutation,
  useReorderPaymentMethodsMutation,
  useUploadPaymentMethodLogoMutation,
  useDeletePaymentMethodLogoMutation,
  useDeletePaymentMethodMutation,
} = paymentMethodApi;
